// MonitoredCompaniesSection.jsx
//
// Renders inside ApiUsagePanel ABOVE its existing tracker UI.
// - Big red banner if any active (undismissed) alerts exist
// - Collapsible "Monitored Companies" panel with last-7-day totals
// - "Manage…" button (Bangert ExtUsers only) opens MonitoredCompaniesAdmin modal

import React, { useState, useEffect, useCallback } from 'react';
import MonitoredCompaniesAdmin from './MonitoredCompaniesAdmin';

const API_BASE = window.location.hostname === 'localhost'
  ? 'http://localhost:3001'
  : '';

const ChevronIcon = ({ open }) => (
  <svg
    className={`w-3 h-3 transition-transform ${open ? 'rotate-90' : ''}`}
    fill="none" stroke="currentColor" viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

function topN(obj, n = 5) {
  if (!obj) return [];
  return Object.entries(obj).sort((a, b) => b[1] - a[1]).slice(0, n);
}

const MonitoredCompaniesSection = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adminOpen, setAdminOpen] = useState(false);
  const [responseUserId, setResponseUserId] = useState('');
  const [rerunningKey, setRerunningKey] = useState(null);

  // Mirror ApiUsagePanel's pattern: pick up userId from auth event
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.userId) setResponseUserId(e.detail.userId);
    };
    window.addEventListener('intacct-auth-userid', handler);
    return () => window.removeEventListener('intacct-auth-userid', handler);
  }, []);

  const isBangertAdmin = responseUserId.startsWith('ExtUser|bangertinc');

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [aRes, sRes] = await Promise.all([
        fetch(`${API_BASE}/api/usage-monitor/active-alerts`),
        fetch(`${API_BASE}/api/usage-monitor/summary?days=7`),
      ]);
      const a = await aRes.json();
      const s = await sRes.json();
      setAlerts(a.alerts || []);
      setSummary(s.rows || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const dismissAlert = async (companyId, date) => {
    await fetch(`${API_BASE}/api/usage-monitor/alerts/${companyId}/${date}/dismiss`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dismissedBy: responseUserId || 'unknown' }),
    });
    refresh();
  };

  const toggleDetail = async (companyId, date) => {
    const key = `${companyId}|${date}`;
    if (expanded[key]) {
      setExpanded((e) => { const n = { ...e }; delete n[key]; return n; });
      return;
    }
    const res = await fetch(`${API_BASE}/api/usage-monitor/detail/${companyId}/${date}`);
    const data = await res.json();
    setExpanded((e) => ({ ...e, [key]: data }));
  };
  const rerunRow = async (companyId, date) => {
    const adminKey = sessionStorage.getItem('drl5k_admin_key');
    if (!adminKey) {
      window.alert ? null : null; // Office sandbox blocks alert anyway
      // Open admin modal to trigger the key prompt
      setAdminOpen(true);
      return;
    }
    const key = `${companyId}|${date}`;
    setRerunningKey(key);
    try {
      const res = await fetch(`${API_BASE}/api/usage-monitor/rerun/${companyId}/${date}`, {
        method: 'POST',
        headers: { 'x-admin-key': adminKey },
      });
      if (res.status === 403) {
        // Stale admin key — open modal to re-prompt
        sessionStorage.removeItem('drl5k_admin_key');
        setAdminOpen(true);
        return;
      }
      // Server runs the job async and responds immediately. Poll for ~30 seconds
      // for the row to update before refreshing the view.
      await new Promise((r) => setTimeout(r, 5000));
      await refresh();
      // Drop any cached detail for this row so the next click re-fetches
      setExpanded((e) => { const n = { ...e }; delete n[key]; return n; });
    } catch (err) {
      setError(err.message);
    } finally {
      setRerunningKey(null);
    }
  };

  // Don't render anything if not connected — no signal to show
  if (!responseUserId) return null;

  return (
    <>
      {/* Red banner */}
      {alerts.length > 0 && (
        <div className="mb-3 rounded border-2 border-red-500 bg-red-500/10 p-3">
          <div className="flex items-center gap-2 mb-2 text-red-400">
            <span className="text-lg">⚠️</span>
            <span className="text-xs font-bold uppercase">High API Usage Detected</span>
          </div>
          <ul className="space-y-1">
            {alerts.map((a) => (
              <li key={`${a.company_id}|${a.usage_date}`} className="text-xs text-red-300">
                <strong className="text-red-200">{a.display_name}</strong>:{' '}
                <span className="font-mono text-red-200">{a.total_calls.toLocaleString()}</span>{' '}
                calls on {a.usage_date}
                <button
                  onClick={() => dismissAlert(a.company_id, a.usage_date)}
                  className="ml-2 text-[10px] text-red-300 underline hover:text-red-100"
                >
                  dismiss
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Collapsible panel */}
      <div className="mb-3 bg-gray-800 rounded border border-gray-700">
        <button
          onClick={() => setCollapsed((c) => !c)}
          className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-750"
        >
          <div className="flex items-center gap-2">
            <ChevronIcon open={!collapsed} />
            <span className="text-xs font-bold uppercase text-cyan-400">Monitored Companies</span>
            <span className="text-[10px] text-gray-500">
              (last 7 days{alerts.length > 0 ? ` • ${alerts.length} alert${alerts.length > 1 ? 's' : ''}` : ''})
            </span>
          </div>
          {isBangertAdmin && (
            <span
              onClick={(e) => { e.stopPropagation(); setAdminOpen(true); }}
              className="text-[10px] text-yellow-400 hover:text-yellow-300 border border-yellow-400/30 rounded px-2 py-0.5 bg-yellow-400/10"
            >
              Manage
            </span>
          )}
        </button>

        {!collapsed && (
          <div className="border-t border-gray-700 px-3 py-2">
            {loading && <div className="text-[10px] text-gray-500 py-2">Loading…</div>}
            {error && <div className="text-[10px] text-red-400 py-2">Error: {error}</div>}
            {!loading && summary.length === 0 && !error && (
              <div className="text-[10px] text-gray-500 py-2">
                No usage data yet. {isBangertAdmin ? 'Click Manage to add companies.' : 'Check back after the next nightly run.'}
              </div>
            )}
            {!loading && summary.length > 0 && (
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-gray-500 border-b border-gray-700">
                    <th className="text-left py-1 pr-2 font-medium">Company</th>
                    <th className="text-left py-1 pr-2 font-medium">Date</th>
                    <th className="text-right py-1 pr-2 font-medium">Calls</th>
                    <th className="text-left py-1 pr-2 font-medium">Status</th>
                    <th className="py-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {summary.map((r) => {
                    const key = `${r.company_id}|${r.usage_date}`;
                    const isAlert = r.alert_triggered === 1;
                    const isDismissed = r.alert_dismissed === 1;
                    const detail = expanded[key];
                    return (
                      <React.Fragment key={key}>
                        <tr className={`border-b border-gray-700/50 ${isAlert && !isDismissed ? 'bg-red-500/5' : ''}`}>
                          <td className="py-1 pr-2 text-gray-200">{r.display_name}</td>
                          <td className="py-1 pr-2 text-gray-400 font-mono">{r.usage_date}</td>
                          <td className={`py-1 pr-2 text-right font-mono ${isAlert ? 'text-red-400 font-bold' : 'text-gray-300'}`}>
                            {r.total_calls.toLocaleString()}
                          </td>
                          <td className="py-1 pr-2">
                            {r.run_error ? (
                              <span className="text-red-400" title={r.run_error}>error</span>
                            ) : isAlert && !isDismissed ? (
                              <span className="text-red-400 font-bold">over threshold</span>
                            ) : isAlert ? (
                              <span className="text-gray-500">dismissed</span>
                            ) : (
                              <span className="text-emerald-400">ok</span>
                            )}
                          </td>
                          <td className="py-1 text-right whitespace-nowrap">
                            {!r.run_error && (
                              <button
                                onClick={() => toggleDetail(r.company_id, r.usage_date)}
                                className="text-cyan-400 hover:text-cyan-300 text-[10px] mr-2"
                              >
                                {detail ? 'hide' : 'detail'}
                              </button>
                            )}
                            {isBangertAdmin && (
                              <button
                                onClick={() => rerunRow(r.company_id, r.usage_date)}
                                disabled={rerunningKey === `${r.company_id}|${r.usage_date}`}
                                className="text-yellow-400 hover:text-yellow-300 text-[10px] disabled:opacity-50"
                                title="Re-run the query for this date (bypasses cache)"
                              >
                                {rerunningKey === `${r.company_id}|${r.usage_date}` ? 'running…' : 're-run'}
                              </button>
                            )}
                          </td>
                        </tr>
                        {detail && detail.success && (
                          <tr className="bg-gray-900/50 border-b border-gray-700/50">
                            <td colSpan={5} className="px-2 py-2">
                              <div className="grid grid-cols-3 gap-3 text-[10px]">
                                <DetailColumn title="Top users" rows={topN(detail.breakdown?.byLogin)} />
                                <DetailColumn title="Top partners" rows={topN(detail.breakdown?.byPartner)} />
                                <DetailColumn title="Top functions" rows={topN(detail.breakdown?.byFunction)} />
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      <MonitoredCompaniesAdmin
        isOpen={adminOpen}
        onClose={() => { setAdminOpen(false); refresh(); }}
        apiBase={API_BASE}
      />
    </>
  );
};

const DetailColumn = ({ title, rows }) => (
  <div>
    <div className="font-bold text-gray-400 uppercase mb-1">{title}</div>
    {rows.length === 0 ? (
      <div className="text-gray-600">—</div>
    ) : (
      <table className="w-full">
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k}>
              <td className="pr-2 text-gray-300 truncate max-w-[120px]" title={k}>{k}</td>
              <td className="text-right text-gray-200 font-mono">{v.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

export default MonitoredCompaniesSection;